/**
 * Development Environment Page
 *
 * Full-screen development environment with terminal and preview panels.
 * Uses the session daemon's WebSocket connection for terminal access.
 */

import {
  AgentChatPanel,
  Button,
  Center,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type ElementInfo,
  PreviewPanel,
  ProvisioningView,
  SplitPane,
  Stack,
  Text,
  TopBar,
} from '@cortex/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';
import { useRequestByNumber } from '@/hooks/use-request-queries';
import { useSessionByRequest } from '@/hooks/use-session-queries';
import { mapProvisioningStageToSteps } from '@/lib/mapProvisioningStageToSteps';
import { createClient } from '@/lib/supabase/client';

export const Route = createFileRoute('/_app/_main/requests/$id/develop')({
  component: DevelopPage,
});

function DevelopPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fetchWithAuth = useFetchWithAuth();

  const [isPausing, setIsPausing] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [elementContext, setElementContext] = useState<{
    description: string;
  } | null>(null);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  // Get access token for WebSocket connection (auth-only, not data)
  useQuery({
    queryKey: ['auth-token'],
    queryFn: async () => {
      const supabase = createClient();
      const { data } = await supabase.auth.getSession();
      setAccessToken(data.session?.access_token ?? null);
      return data.session?.access_token ?? null;
    },
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes to keep token fresh
  });

  // Fetch request via API
  const { data: request } = useRequestByNumber(id);

  // Fetch session via shared hook (deduplicates queryKey across views)
  const {
    data: session,
    isPending: isLoadingSession,
    refetch: refetchSession,
  } = useSessionByRequest(request?.id);

  // Handle pause/resume
  const handlePause = useCallback(async () => {
    if (!session) return;

    setIsPausing(true);
    try {
      const endpoint =
        session.status === 'suspended'
          ? `/sessions/${session.id}/resume`
          : `/sessions/${session.id}/suspend`;

      const response = await fetchWithAuth(endpoint, { method: 'POST' });
      if (!response.ok) throw new Error('Failed to update session');

      await refetchSession();
    } catch (error) {
      console.error('Pause/resume failed:', error);
    } finally {
      setIsPausing(false);
    }
  }, [session, fetchWithAuth, refetchSession]);

  // Handle finalize
  const handleFinalize = useCallback(async () => {
    if (!session || !request) return;

    setIsFinalizing(true);
    try {
      const response = await fetchWithAuth(`/sessions/${session.id}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          branchName: `cortex/request-${request.request_number}`,
        }),
      });

      if (!response.ok) throw new Error('Failed to finalize session');

      // Invalidate request cache and navigate back
      queryClient.invalidateQueries({ queryKey: ['request', id] });
      navigate({
        to: '/requests/$id',
        params: { id: String(request.request_number) },
      });
    } catch (error) {
      console.error('Finalize failed:', error);
    } finally {
      setIsFinalizing(false);
    }
  }, [session, request, fetchWithAuth, queryClient, navigate, id]);

  // Handle reset - destroys session and stays in development (can start new session)
  const handleReset = useCallback(async () => {
    if (!session || !request) return;

    setShowResetModal(false);
    setIsResetting(true);
    try {
      // Destroy the session
      const destroyResponse = await fetchWithAuth(
        `/sessions/${session.id}/destroy`,
        { method: 'POST' }
      );

      if (!destroyResponse.ok) {
        // Non-fatal: destroy failed but we still invalidate cache to reset UI
      }

      // Invalidate session cache - component will show "Start Session" button
      queryClient.invalidateQueries({ queryKey: ['dev-session', request.id] });
    } catch (error) {
      console.error('Reset failed:', error);
    } finally {
      setIsResetting(false);
    }
  }, [session, request, fetchWithAuth, queryClient]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (request) {
      navigate({
        to: '/requests/$id',
        params: { id: String(request.request_number) },
      });
    } else {
      navigate({ to: '/' });
    }
  }, [request, navigate]);

  // Handle element selection from preview
  const handleElementSelected = useCallback(
    (_info: ElementInfo, description: string) => {
      setElementContext({ description });
    },
    []
  );

  const handleElementContextConsumed = useCallback(() => {
    setElementContext(null);
  }, []);

  const handleUndoCompleted = useCallback(() => {
    setPreviewRefreshKey((prev) => prev + 1);
  }, []);

  // Loading state
  if (!request || isLoadingSession) {
    return (
      <Center className="bg-surface-1 h-screen">
        <Text color="muted">Loading...</Text>
      </Center>
    );
  }

  // No session state
  if (!session) {
    return (
      <Center className="bg-surface-1 h-screen">
        <Stack gap="md" align="center">
          <Text color="muted">No active session found</Text>
          <Button variant="link" onClick={handleBack}>
            Go back to request
          </Button>
        </Stack>
      </Center>
    );
  }

  // Provisioning state
  if (session.status === 'provisioning') {
    return (
      <Stack gap="none" className="bg-surface-1 h-screen">
        <TopBar
          requestTitle={request.title}
          sessionStatus={session.status}
          onPause={handlePause}
          onFinalize={handleFinalize}
          onBack={handleBack}
          sessionStartedAt={session.createdAt}
        />
        <Stack gap="none" className="flex-1">
          <ProvisioningView
            steps={mapProvisioningStageToSteps(
              session.provisioningStage,
              session.provisioningError
            )}
          />
        </Stack>
      </Stack>
    );
  }

  // Failed state
  if (session.status === 'failed') {
    return (
      <Stack gap="none" className="bg-surface-1 h-screen">
        <TopBar
          requestTitle={request.title}
          sessionStatus={session.status}
          onPause={handlePause}
          onFinalize={handleFinalize}
          onBack={handleBack}
          sessionStartedAt={session.createdAt}
        />
        <Center className="flex-1">
          <Stack gap="md" align="center">
            <AlertTriangle className="text-error h-10 w-10" />
            <Text color="muted">
              {session.provisioningError ?? 'Provisioning failed'}
            </Text>
            <Button variant="outline" onClick={() => void handleReset()}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
          </Stack>
        </Center>
      </Stack>
    );
  }

  // Build WebSocket URL for the API proxy (not direct to machine)
  // The proxy runs on the API server and relays to internal Fly machines
  const apiBaseUrl =
    import.meta.env.VITE_API_URL || 'https://aiq-cortex-api.fly.dev';
  const wsBaseUrl = apiBaseUrl
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  const proxyWebsocketUrl =
    session.id && accessToken
      ? `${wsBaseUrl}/sessions/${session.id}/ws?token=${encodeURIComponent(accessToken)}`
      : null;

  return (
    <Stack gap="none" className="bg-surface-1 h-screen">
      <TopBar
        requestTitle={request.title}
        sessionStatus={session.status}
        onPause={handlePause}
        onFinalize={handleFinalize}
        onBack={handleBack}
        onReset={() => setShowResetModal(true)}
        isPausing={isPausing}
        isFinalizing={isFinalizing}
        isResetting={isResetting}
        sessionStartedAt={session.createdAt}
      />
      <Stack gap="none" className="flex-1 overflow-hidden">
        <SplitPane
          left={
            <AgentChatPanel
              websocketUrl={proxyWebsocketUrl}
              sessionStatus={session.status}
              elementContext={elementContext}
              onElementContextConsumed={handleElementContextConsumed}
              onUndoCompleted={handleUndoCompleted}
              sessionId={session.id}
              fetchWithAuth={fetchWithAuth}
            />
          }
          right={
            <PreviewPanel
              key={previewRefreshKey}
              previewUrl={session.previewUrl}
              sessionStatus={session.status}
              onElementSelected={handleElementSelected}
            />
          }
          defaultLeftWidth={35}
        />
      </Stack>

      {/* Reset Confirmation Modal */}
      <Dialog
        open={showResetModal}
        onOpenChange={(open) => {
          if (!open) setShowResetModal(false);
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Reset Development Session</DialogTitle>
            <DialogDescription>
              Confirm you want to destroy the current session and start fresh.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Stack gap="sm">
              <Text color="secondary">
                Are you sure you want to reset this development session?
              </Text>
              <Text color="muted" size="sm">
                The session will be destroyed and you can start a new one. All
                uncommitted changes will be lost.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleReset()}
              disabled={isResetting}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
